import type { ArchivedConversation, ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { showToast } from '@crowdex/ui/components/toaster';
import {
  Archive,
  Blocks,
  ChartColumn,
  FileText,
  FolderOpen,
  FolderPlus,
  Keyboard,
  LayoutGrid,
  LibraryBig,
  Link2,
  MessageSquare,
  Plus,
  Settings,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { ItemIcon } from '@/components/item-icon';
import { listAppArtifacts } from '@/features/apps/artifacts-client';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { appHomePath, openAppHome } from '@/lib/app-home';
import { useWorkspaceDirectory, workspaceDirectoryStore } from '@/lib/assistants/directory';
import { openWorkspaceDialog } from '@/lib/assistants/workspace-dialogs';
import {
  listArchivedConversations,
  searchConversationMessages,
} from '@/lib/conversations/conversation-changes-client';
import { dockApp } from '@/lib/desktop-shell/app-panels';
import {
  PREVIEW_ARCHIVED,
  PREVIEW_SEARCH_ARTIFACTS,
  PREVIEW_SEARCH_FILES,
  PREVIEW_SEARCH_MESSAGES,
} from '@/lib/desktop-shell/preview-shell-data';
import { formatAge } from '@/lib/desktop-shell/sidebar-model';
import { useSidebarSearch } from '@/lib/desktop-shell/sidebar-search';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { useWorkspaceName } from '@/lib/desktop-shell/use-workspace-name';
import { useLibraryPage, useRouter } from '@/lib/navigation';
import { type KeyboardShortcut, shortcutLabel } from '@/lib/platform';
import { CONNECTIONS_PATH, pluginPagePath, pluginsPath } from '@/lib/plugins/plugin-sections';
import { currentShortcut, useShortcutOverrides } from '@/lib/settings-page/keyboard-shortcuts';
import {
  listWorkspaceFiles,
  openWorkspaceFileInPanel,
} from '@/lib/side-panel/workspace-file-index';
import { resolveWorkspaceClient } from '@/lib/workspaces/workspace-client';
import { setAllAppsOpen } from './all-apps';
import type { SearchEntry } from './search-model';
import { useShellNavigation } from './shell-navigation';
import { NEW_TASK_SHORTCUT, SETTINGS_SHORTCUT, SHORTCUTS_SHORTCUT } from './shell-shortcuts';
import { recordAppOpened } from './sidebar-preferences';

/** An entry of search & commands, with what choosing it does. */
export type ActionEntry = SearchEntry & {
  icon: ReactNode;
  open: () => void;
  /** It opens a dialog, which keeps the focus once the search closes. */
  opensDialog?: boolean | undefined;
  openSideBySide?: () => void;
};

/** The workspace's files, listed once a minute at most (the Files panel's listing). */
const fileLists = new Map<string, { at: number; files: Promise<string[]> }>();
const FILE_LIST_TTL_MS = 60_000;

function workspaceFiles(store: DesktopShellStore, workspace: string | null): Promise<string[]> {
  const { cloudRuntime } = store.getState();
  if (workspace === null) return Promise.resolve([]);
  const key = `${cloudRuntime?.id ?? 'here'}:${workspace}`;
  const cached = fileLists.get(key);
  if (cached !== undefined && Date.now() - cached.at < FILE_LIST_TTL_MS) return cached.files;
  const files = (async () => {
    const client = resolveWorkspaceClient(cloudRuntime, workspace);
    try {
      await client.ready;
      return await listWorkspaceFiles((path) => client.listDirectory(path));
    } finally {
      client.close();
    }
  })().catch(() => [] as string[]);
  fileLists.set(key, { at: Date.now(), files });
  return files;
}

/** What the conversations' messages say, searched on the server as the user types. */
function useMessageHits(query: string, open: boolean, preview: boolean) {
  const [hits, setHits] = useState<readonly { id: string; snippet: string; title?: string }[]>([]);
  useEffect(() => {
    const needle = query.trim();
    if (!open || needle.length < 2) {
      setHits([]);
      return;
    }
    if (preview) {
      setHits(
        PREVIEW_SEARCH_MESSAGES.filter((hit) =>
          hit.snippet.toLocaleLowerCase().includes(needle.toLocaleLowerCase()),
        ),
      );
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void searchConversationMessages(needle, controller.signal)
        .then(setHits)
        .catch(() => undefined);
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, open, preview]);
  return hits;
}

/**
 * "Start a conversation: “…”" (D21): what was typed and found nothing becomes the draft of a new
 * conversation, its composer ready to send it.
 */
export function useStartConversation(): (text: string) => void {
  const navigation = useShellNavigation();
  const library = useLibraryPage();
  const store = useDesktopShellStoreApi();
  return (text) => {
    setAllAppsOpen(false);
    library.leave();
    navigation.newTask();
    const { chatId, setPaneDrafts, setComposerFocusRequest } = store.getState();
    setPaneDrafts((current) => ({ ...current, [chatId]: text.trim() }));
    // Its composer takes the keys, the caret after the draft, ready to send it.
    setComposerFocusRequest((request) => request + 1);
  };
}

/** Every entry search & commands (D21) offers while open, for what is typed. */
export function useSearchEntries(open: boolean, query: string): ActionEntry[] {
  const navigation = useShellNavigation();
  const router = useRouter();
  const library = useLibraryPage();
  const store = useDesktopShellStoreApi();
  const workspace = useDesktopShellStore((state) => state.workspace);
  const workspaceName = useWorkspaceName(workspace);
  const workbench = useWorkbench();
  const page = useSidebarSearch().source;
  // Its commands show the user's keys.
  const shortcuts = useShortcutOverrides();
  const [artifacts, setArtifacts] = useState<readonly ArtifactListingEntry[]>([]);
  const [archived, setArchived] = useState<readonly ArchivedConversation[]>([]);
  const projects = useWorkspaceDirectory((state) => state.projects);
  const [files, setFiles] = useState<readonly string[]>([]);
  const hits = useMessageHits(query, open, navigation.preview);
  useEffect(() => {
    if (!open) return;
    if (navigation.preview) {
      setArchived(PREVIEW_ARCHIVED);
      setFiles(PREVIEW_SEARCH_FILES);
      return;
    }
    let active = true;
    void listAppArtifacts()
      .then((entries) => active && setArtifacts(entries))
      .catch(() => undefined);
    void listArchivedConversations()
      .then((entries) => active && setArchived(entries))
      .catch(() => undefined);
    void workspaceFiles(store, workspace).then((entries) => active && setFiles(entries));
    return () => {
      active = false;
    };
  }, [open, navigation.preview, store, workspace]);

  return useMemo(() => {
    // A command's keys as menus write them: the user's, else its own.
    const commandLabel = (id: string, fallback: KeyboardShortcut) =>
      shortcutLabel(currentShortcut(id, shortcuts) ?? fallback);
    const leave = () => {
      setAllAppsOpen(false);
      library.leave();
    };
    const go = (href: string) => () => {
      setAllAppsOpen(false);
      router.push(href);
    };
    const said = new Map(hits.map((hit) => [hit.id, hit]));
    const listed = new Set(navigation.threads.map((thread) => thread.id));
    const conversations: ActionEntry[] = [...navigation.threads]
      .sort((left, right) => right.updatedAt - left.updatedAt)
      .map((thread) => {
        const hit = said.get(thread.id);
        return {
          id: `conversation:${thread.id}`,
          kind: 'conversations',
          label: thread.title,
          // What its messages say, when that is what matched.
          detail: hit?.snippet ?? thread.project,
          keywords: hit === undefined ? undefined : [query],
          meta: formatAge(thread.updatedAt),
          icon: <MessageSquare />,
          open: () => {
            leave();
            navigation.openThread(thread);
          },
          ...(navigation.openSideBySide !== undefined && thread.id !== navigation.activeThreadId
            ? {
                openSideBySide: () => {
                  leave();
                  navigation.openSideBySide?.(thread);
                },
              }
            : {}),
        };
      });
    // Found by their messages, not listed in the sidebar (older ones).
    for (const hit of hits)
      if (!listed.has(hit.id) && hit.title !== undefined)
        conversations.push({
          id: `conversation:${hit.id}`,
          kind: 'conversations',
          label: hit.title,
          detail: hit.snippet,
          keywords: [query],
          icon: <MessageSquare />,
          open: () => {
            leave();
            navigation.openConversation(hit.id);
          },
        });
    const artifactEntries: ActionEntry[] = navigation.preview
      ? PREVIEW_SEARCH_ARTIFACTS.map((artifact) => ({
          id: artifact.id,
          kind: 'artifacts',
          label: artifact.title,
          detail: artifact.detail,
          icon: <ChartColumn />,
          open: () => undefined,
        }))
      : artifacts.map((artifact) => ({
          id: `artifact:${artifact.id}`,
          kind: 'artifacts',
          label: artifact.title,
          detail: artifact.typeTitle,
          meta: formatAge(Date.parse(artifact.modifiedAt)),
          keywords: [artifact.path],
          icon: <LibraryBig />,
          // Outside a conversation an item opens full screen, on its plugin's page.
          open: go(appHomePath(artifact.plugin, artifact.id)),
        }));
    const project = workspaceName ?? undefined;
    const fileEntries: ActionEntry[] = files.map((path) => ({
      id: `file:${path}`,
      kind: 'files',
      label: path,
      ...(project === undefined ? {} : { detail: project }),
      icon: <FileText />,
      open: () => {
        leave();
        store.getState().setSidePanelOpen(true);
        openWorkspaceFileInPanel(path);
      },
    }));
    const archivedEntries: ActionEntry[] = archived.map((entry) => ({
      id: `archived:${entry.id}`,
      kind: 'archived',
      label: entry.title,
      detail: 'Archived · ↵ brings it back',
      meta: formatAge(Date.parse(entry.updatedAt)),
      icon: <Archive />,
      open: () =>
        void navigation
          .unarchiveConversation?.(entry.id)
          .then(() => {
            setArchived((current) => current.filter((item) => item.id !== entry.id));
            showToast({ title: `“${entry.title}” is back in the sidebar.`, tone: 'success' });
          })
          .catch(() => showToast({ title: 'It stayed archived.', tone: 'error' })),
    }));
    const archivedProjects: ActionEntry[] = projects
      .filter((project) => project.archivedAt !== null)
      .map((project) => ({
        id: `archived-project:${project.id}`,
        kind: 'archived',
        label: project.name,
        detail: 'Archived assistant · ↵ brings it back',
        meta: formatAge(Date.parse(project.archivedAt ?? project.updatedAt)),
        icon: <Archive />,
        open: () =>
          void workspaceDirectoryStore()
            .getState()
            .updateAssistant(project.id, { archived: false })
            .then(() =>
              showToast({ title: `“${project.name}” is back in the sidebar.`, tone: 'success' }),
            )
            .catch(() => showToast({ title: 'It stayed archived.', tone: 'error' })),
      }));
    const commands: ActionEntry[] = [
      // The page's own items (a settings section, a plugin): they are its commands.
      ...(page?.groups ?? []).flatMap((group) =>
        group.items.map(
          (item): ActionEntry => ({
            id: `page:${group.heading}:${item.id}`,
            kind: 'commands',
            label: item.label,
            detail: item.meta ?? group.heading,
            keywords: item.keywords,
            icon: item.icon ?? <Settings />,
            open: () => page?.onSelect(item),
          }),
        ),
      ),
      {
        id: 'command:new-task',
        kind: 'commands',
        label: 'New conversation',
        detail: commandLabel('new-task', NEW_TASK_SHORTCUT),
        always: true,
        icon: <Plus />,
        open: () => {
          leave();
          navigation.newTask();
        },
      },
      ...(navigation.addRepository === undefined
        ? []
        : [
            {
              id: 'command:add-repository',
              kind: 'commands' as const,
              label: 'Add repository…',
              detail: 'A folder or a clone',
              keywords: ['clone', 'folder', 'open'],
              icon: <FolderOpen />,
              opensDialog: true,
              open: () => navigation.addRepository?.(),
            },
          ]),
      {
        id: 'command:new-project',
        kind: 'commands',
        label: 'New assistant',
        icon: <FolderPlus />,
        opensDialog: true,
        open: () => openWorkspaceDialog({ kind: 'new-project' }),
      },
      {
        id: 'command:library',
        kind: 'commands',
        label: 'Library',
        icon: <LibraryBig />,
        open: go('/artifacts'),
      },
      {
        id: 'command:apps',
        kind: 'commands',
        label: 'All apps',
        icon: <LayoutGrid />,
        open: () => {
          library.leave();
          setAllAppsOpen(true);
        },
      },
      {
        id: 'command:plugins',
        kind: 'commands',
        label: 'Plugins',
        icon: <Blocks />,
        open: go(pluginsPath()),
      },
      {
        id: 'command:connections',
        kind: 'commands',
        label: 'Connections',
        icon: <Link2 />,
        open: go(CONNECTIONS_PATH),
      },
      {
        id: 'command:settings',
        kind: 'commands',
        label: 'Settings',
        detail: commandLabel('settings', SETTINGS_SHORTCUT),
        icon: <Settings />,
        open: go('/settings'),
      },
      {
        id: 'command:shortcuts',
        kind: 'commands',
        label: 'Keyboard shortcuts',
        detail: commandLabel('keyboard-shortcuts', SHORTCUTS_SHORTCUT),
        icon: <Keyboard />,
        open: go('/settings?section=shortcuts'),
      },
      // Search is not a conversation's: the apps open full screen; the core's tools (the
      // browser, the terminal) are a conversation's own, and dock in its panel.
      ...workbench.panels.map((panel): ActionEntry => {
        const app = panel.appView !== undefined || panel.remoteView !== undefined;
        return {
          id: `command:panel:${panel.view}`,
          kind: 'commands',
          label: `Open ${panel.title}`,
          detail: app ? 'App' : 'In the panel',
          icon: <ItemIcon icon={pluginIcon(panel.icon ?? 'app')} src={panel.iconSrc} />,
          open: () => {
            recordAppOpened(panel.view);
            if (app) return openAppHome(panel.plugin);
            leave();
            dockApp(store, panel.view);
          },
        };
      }),
      // A service has no page: it is set up, and its use seen, on its settings page.
      ...(workbench.services ?? []).map(
        (service): ActionEntry => ({
          id: `command:service:${service.plugin}`,
          kind: 'commands',
          label: service.title,
          detail: 'Service',
          icon: <ItemIcon icon={Blocks} src={service.iconSrc} />,
          open: go(pluginPagePath('installed', service.plugin)),
        }),
      ),
    ];
    return [
      ...conversations,
      ...artifactEntries,
      ...fileEntries,
      ...commands,
      ...archivedEntries,
      ...archivedProjects,
    ];
  }, [
    archived,
    artifacts,
    files,
    hits,
    library,
    navigation,
    page,
    projects,
    query,
    router,
    shortcuts,
    store,
    workbench.panels,
    workbench.services,
    workspaceName,
  ]);
}
